import Backbone from 'backbone';

import _ from 'underscore';
import ko from 'knockout';
import Promise from 'bluebird';

import { SearchBox } from '@bingads-webui/search-box';
import { helpers } from '@bingads-webui/deterministic-view';
import { deflickerPromise } from '@bingads-webui/deflicker';

// grid
import pgrid, {
  GridView,
  projections,
  layout,
} from 'projection-grid';

import { PillControl } from './pill-control/index';

import template from './template/index.pug';
import ViewModel from './view-model';


const inputDebounceWait = 300;

const {
  RowTriStateCheckboxProjection: CheckboxProjection,
  ColumnQueryable: ColumnQueryableProjection,
  Sink: SourceProjection,
} = projections;

const {
  TableLayout,
  templates: { table: GridTemplate },
  renderers: { Virtualization },
} = layout;

/**
 * Enumerates the values for the fixed view filter types
 * @typedef {Object} Selector~ViewFilter
 * @property {String} all
 * @property {String} selected
 * @property {String} changed
 */
const ViewFilter = {
  all: 'all',
  selected: 'selected',
  changed: 'changed',
};

/**
 * Enumerates the values for the check states
 * @typedef {Object} Selector~CheckState
 * @property {String} checked
 * @property {String} unchecked
 * @property {String} indeterminate
 */
const CheckState = {
  checked: 'checked',
  unchecked: 'unchecked',
  indeterminate: 'indeterminate',
};

/**
 * For the specified check state, determines the next check state
 * @callback Selector~TransitionFunction
 * @param {Selector~CheckState} checkState - the current check state
 * @returns {Selector~CheckState} the next check state
 */

/**
 * Enumerates the predefined check transition functions.
 * State transition depends on the current state and whether a short or full cycle is requested.
 * When a short cycle is requested, the checkbox cannot return to the indeterminate state,
 * event if it started in this state.
 * Two directions of transition are available:
 * - ... -> indeterminate -> checked -> unchecked -> ...
 * - ... -> indeterminate -> unchecked -> checked -> ...
 *
 * @typedef {Object} Selector~CheckTransition
 * @property {Selector~TransitionFunction} indeterminateToCheckedFullCycle
 * @property {Selector~TransitionFunction} indeterminateToUncheckedFullCycle
 * @property {Selector~TransitionFunction} indeterminateToCheckedShortCycle
 * @property {Selector~TransitionFunction} indeterminateToUncheckedShortCycle
 */
const CheckTransition = {
  indeterminateToCheckedFullCycle:
    CheckboxProjection.CheckTransitionRule.indeterminateToCheckedFullCycle,
  indeterminateToUncheckedFullCycle:
    CheckboxProjection.CheckTransitionRule.indeterminateToUncheckedFullCycle,
  indeterminateToCheckedShortCycle:
    CheckboxProjection.CheckTransitionRule.indeterminateToCheckedShortCycle,
  indeterminateToUncheckedShortCycle:
    CheckboxProjection.CheckTransitionRule.indeterminateToUncheckedShortCycle,
};

const defaultCheckAllState = {
  state: CheckState.indeterminate,
  transition: CheckTransition.indeterminateToCheckedFullCycle,
};

function takeTop(limit, items) {
  return items.slice(0, limit);
}

function createCheckAllCheckMap(stats, checkAll, items) {
  const targetCheckState = _.defaults(defaultCheckAllState, checkAll);
  const notChanged = item => !_.has(stats.changed, item.Id);

  return _.chain(items)
    .filter(notChanged)
    .map(item =>
      [item.Id, {
        id: item.Id,
        state: targetCheckState.state,
        transition: targetCheckState.transition,
      }])
    .object()
    .value();
}

function stopEventPropogation(event) {
  event.stopPropagation();
  const { isForVNext } = this.options;
  if (isForVNext) {
    event.preventDefault();
  }
}

/**
 * The Backbone View class.
 * @external "Backbone.View"
 * @see {@link http://backbonejs.org/#View|Backbone View}
 */

/** The projection grid
 * @external projection-grid
 * @see {@link https://github.com/Microsoft/projection-grid#projection-grid|Projection grid}
 * */

/**
 * A simple object describing an item's check state and check state transition rule
 * @typedef {Object} Selector~CheckMapItem
 * @property {(String|Symbol)} id - the item's ID
 * @property {Selector~CheckState} state - the check state
 * @property {Selector~TransitionFunction} transition - the check state transition function
 */

/**
 * A key/value map, where keys are item IDs and values are the respective
 * items' check states represented by {@link Selector~CheckMapItem} objects
 * @typedef {Object.<(String|Symbol), Selector~CheckMapItem>} Selector~CheckMap
 */

/**
 * A simple record object describing the statistics on the selected items.
 * @typedef {Object} Selector~FullStat
 * @property {Selector~CheckMapItem[]} checked - all checked items
 * @property {Selector~CheckMapItem[]} unchecked - all unchecked items, which changed their
 * state since the last data load
 * @property {Selector~CheckMapItem[]} indeterminate - all items in the indeterminate state,
 * which changed their state since the last data load or pre-check initialization
 * @property {Selector~CheckMap} added - ones that got into checked or indeterminate state
 * from unchecked
 * @property {Selector~CheckMap} changed - the added + removed + the ones that changed their
 * checkbox state, when compared to the initial state
 * @property {Selector~CheckMap} removed - the ones that got unchecked
 * @property {Selector~CheckMap} unchanged - the ones, which check state hasn't changed,
 * when compared to the initial state
 * @property {Function} hasChanges - determines if the current object shows any changes
 * from the initial state
 */

/**
 * Signals the finishing of data fetching
 * @event Selector#load:data
 * @type {Object}
 * @property {Boolean} hasData - Indicates whether any items were fetched
 */

/**
 * Signals any changes to the selection caused by the user
 * @event Selector#change:selection
 * @type {Selector~FullStat}
 */

/**
 * The forwarded grid events. Useful to synchronize with grid
 * data update and rendering processes.
 * @event Selector#grid:*
 * @type {Any}
 */


/**
 * Applies defaults to the options object.
 *
 * It does it by setting the default values for options
 * which are _undefined_ in the passed in object.
 * @private
 * @param {Object} options - the options object
 * @return {Object} - a shallow copy of the original options object, with defaults applied
*/
function applyDefaults(options) {
  const defaultOptions = {
    isForVNext: false,
    busyIndicator: {
      enabled: false,
      showAfterMs: 500,
      showForMs: 700,
    },
    data: {
      itemsDisplayLimit: 50,
      checkMap: {},
      // checkAll: undefined,
    },
    filter: {
      disableSearch: false,
      showSelected: false,
      showChanged: false,
    },
    showNoDataMessage: false,
    styles: {
      searchIcon: [],
      list: [],
      checkboxColumn: [],
      nameColumn: [],
    },
  };

  const mergedOptions = _.defaults({}, options, defaultOptions);

  _.defaults(mergedOptions.busyIndicator, defaultOptions.busyIndicator);
  _.defaults(mergedOptions.data, defaultOptions.data);
  _.defaults(mergedOptions.filter, defaultOptions.filter);
  _.defaults(mergedOptions.styles, defaultOptions.styles);

  // use default itemsDisplayLimit if current limit is not valid
  if (!_.isFinite(mergedOptions.data.itemsDisplayLimit)
    || mergedOptions.data.itemsDisplayLimit <= 0) {
    mergedOptions.data.itemsDisplayLimit = defaultOptions.data.itemsDisplayLimit;
  }

  return mergedOptions;
}

// Eslint gets confused with the use of quoted symbol references
// eslint-disable-next-line valid-jsdoc
/**
 * The Selector view
 * @extends external:"Backbone.View"
 * @param {Object} options - the options object. Please refere to Backbone's documentation for
 * information on standard options. This control expects only the `className` option to be
 * overriden, if needed.
 * @param {Boolean} [options.isForVNext] - whether it is used in vnext
 * @param {Object} [options.busyIndicator] - the busy indicator related options
 * @param {Boolean} [options.busyIndicator.enabled=false] - the flag specifying whether busy
 * indicator should be shown if it takes more than configured time for the data to load
 * @param {Boolean} [options.busyIndicator.showAfterMs=500] - the time in milliseconds to wait
 * before displaying the busy indicator and message, had the data fetching not
 * finished by that time.
 * @param {Boolean} [options.busyIndicator.showForMs=700] - the minimum amount of time
 * in milliseconds to wait before hiding the busy indicator and message. The busy indicator
 * will continue running until the process finishes, even if this time has elapsed.
 * @param {Object} [options.data] - the data related options
 * @param {Selector~CheckMapItem} [options.data.checkAll=undefined] - the check state
 * description for all the items, to be applied after data is fetched. Use it to, for example,
 * set all items checked or in indeterminate state.
 * @param {Selector~CheckMap} [options.data.checkMap={}] - an object describing the state of items
 * in the selector control, in regards to their checked/unchecked/indeterminate state, and
 * the respective check-state transition rules
 * @param {int} [options.data.itemsDisplayLimit=50] - Maximum number of items to display. If there
 * are number of items is greater than the limit, an alert is displayed at the bottom of the list.
 * @param {Object} [options.filter] - the filter related options. If either of showSelected or
 * showChanged is true, the 'All' fixed filter will also be shown. If neither of the fixed
 * filter options is true, then fixed filter bar will be hidden
 * @param {Object} [options.filter.showSelected=false] - the flag that specifies whether
 * to show the 'N selected' fixed filter
 * @param {Object} [options.filter.showChanged=false] - the flag that specifies whether
 * to show the 'N changed' fixed filter
 * @param {Boolean} [options.filter.disableSearch=false] - the flag that specifies whether
 * to disable the searchbox filter
 * @param {Object} options.i18n - the i18n provider. Will throw Error if not provided.
 * @param {Boolean} [options.showNoDataMessage=false] - the flag specifying whether to show
 * the 'no data' message if after initial load there are no records
 * @param {Boolean} [options.allowCheckAll=undefined] - flag specifying whether to show a header
 * above the data that allows the user to check all visible checkboxes.
 * @param {Object} [options.styles] - the styling options
 * @param {String[]} [options.styles.searchIcon=[]] - the classes to set on the table element
 * @param {String} [options.styles.searchBoxAriaLabel] - the aria-label to set on the search box
 * @param {String[]} [options.styles.list=[]] - the classes to set on the table element
 * @param {Object} [options.styles.listAttributes] - the attributes to set on the table element
 * @param {String[]} [options.styles.checkboxColumn=[]] - the classes to set on the checkbox cells
 * @param {Object} [options.styles.checkboxAttributes] - the attributes to set on the checkbox cells
 * @param {String[]} [options.styles.nameColumn=[]] - the classes to set on the name cells
 * @param {Object} [options.styles.nameAttributes] - the attributes to set on the name cells
 * @param {Object} [options.templates.nameTemplate]
 *  - the optional custom template for overriding the Name column.
 *  - The template should reference 'model' for the item data,
 *  - and 'property' for a reference to the property name.
 * @fires Selector#load:data
 * @fires Selector#change:selection
 * @fires Selector#grid:*
 * @tutorial default
 */
export class Selector extends Backbone.View {
  /**
   * These can be overridden by passing the `className` option
   * to the view's constructor. The default classes are `"selector dropdown-menu"`.
   * The "dropdown-menu" class allows the control to be blended in with other items
   * in a drop down menu.
   * @summary Gets the default classes
   * @type {String}
   * @readonly
   */
  get className() {
    return 'selector-v2';
  }

  get events() {
    return {
      'click .selector__main': stopEventPropogation,
    };
  }

  /**
   * Gets the current check map
   * @type {Selector~CheckMap}
   * @readonly
   */
  get checkMap() {
    return this.checkbox.get('row.check.map');
  }

  /**
   * Intializes the view based on the options specified.
   * @private
   * @param {Object} options - See {@link Selector#constructor} for more details
   * @return {void}
   */
  initialize(options = {}) {
    if (!options.i18n) {
      throw new Error('i18n object must be provided');
    }

    this.options = applyDefaults(options);
    const { busyIndicator } = this.options;
    const initialViewFilter = ViewFilter.all;

    this.filterModel = new Backbone.Model({
      viewFilter: initialViewFilter,
    });
    this.filterModel.on('change', (model, ...args) => {
      const { viewFilter, searchFilter } = model.attributes;
      window.console.log('filter', model, args);
      this.trigger('request:data', {
        viewFilter,
        searchFilter,
        stats: this.getStats(),
      });
    });

    if (this.options.filter.showChanged ||
      this.options.filter.showSelected) {
      this.filterPill = new PillControl(_.extend({
        selectedItem: initialViewFilter,
        shellClasses: ['pill-control'],
        topClasses: ['pill-control__list--links'],
        itemClasses: ['pill-control__list-item--links'],
      }, this.getFilterPillState()));

      this.filterPill.on('select', _.debounce((data) => {
        this.filterModel.set('viewFilter', data.item.id);
      }, inputDebounceWait));
    }

    if (!this.options.filter.disableSearch) {
      this.searchBox = new SearchBox({
        placeholder: this.options.i18n.getString('Selector_Filter_Placeholder'),
        realtime: true,
        iconClasses: this.options.styles.searchIcon,
        ariaLabel: this.options.styles.searchBoxAriaLabel,
      });
      this.searchBox.on('change:query search', _.debounce((filterVal) => {
        this.filterModel.set('searchFilter', filterVal);
      }, inputDebounceWait));
    }

    this.viewModel = new ViewModel({
      showNoDataMessage: this.options.showNoDataMessage,
    });
    this.withInlineProgress = busyIndicator.enabled ? deflickerPromise.bind(undefined, {
      onDelay: busyIndicator.showAfterMs,
      offDelay: busyIndicator.showForMs,
      on: () => {
        this.viewModel.inProgress(true);
        return () => {
          this.viewModel.inProgress(false);
        };
      },
    }) : Promise.resolve;
    this.initCheckboxes(options.checkboxTemplate);
  }

  /**
   * Sets the items' check map to the passed in check map.
   * It will also reset the preCheckMap to this check map,
   * so further selection statistics will be based on this
   * new check map.
   * @param {Selector~CheckMap} checkMap - the check map
   * @return {void}
   */
  setCheckMap(checkMap) {
    this.preCheckMap = checkMap;
    this.checkbox.set('row.check.map', _.clone(this.preCheckMap));
    // If the data has already been requested and arrived,
    // refresh the check state for 'check all' case.
    // Otherwise, the check state is going to be updated when
    // data arrives
    if (this.p$data && this.p$data.isFulfilled()) {
      this.checkAllIfRequested();
    }
  }

  /**
   * Sets the items for the control to render and work with
   * @param {Object[]|Promise<Object[]>} itemsOrPromise - the array or a promise
   * of the array of data items
   * @return {void}
   */
  setData(itemsOrPromise) {
    this.viewModel.loaded(false);
    this.p$data = this.withInlineProgress(this.loadData(itemsOrPromise))
      .then((items) => {
        this.items = items;
        if (this.viewModel.firstLoad()) {
          this.viewModel.noData(this.items.length === 0);
        }
        this.viewModel.firstLoad(false);
        this.checkAllIfRequested();
        this.viewModel.loaded(true);

        this.viewModel.moreThanLimit(items.length > this.options.data.itemsDisplayLimit);
        this.grid.projection.set('seed', takeTop(this.options.data.itemsDisplayLimit, items));

        const { searchFilter } = this.getFilters();
        const hasSearchFilter = searchFilter && searchFilter.length > 0;

        let searchResultAriaLabel = this.options.i18n.getString(_TL_('Input keywords to see search results'));
        if (hasSearchFilter) {
          searchResultAriaLabel = (items.length > 0) ?
            this.options.i18n.getString(
              'Found {{totalCount}} Results for {{searchText}}',
              { totalCount: items.length, searchText: searchFilter }
            ) :
            this.options.i18n.getString('No results found for {{searchText}}', { searchText: searchFilter });
        }
        this.viewModel.searchResultAriaLabel(searchResultAriaLabel);

        _.defer(() => this.trigger('load:data', { hasData: this.items.length > 0, items: this.items }));
      });
  }

  /**
   * Does the memory clean-up
   * @return {void}
   */
  remove() {
    if (this.searchBox) {
      this.searchBox.remove();
      this.searchBox = null;
    }

    if (this.grid) {
      this.grid.remove();
      this.grid = null;
    }

    super.remove();
  }

  /**
   * Fetches the data items. The items can either be an array of data items
   * or a Promise resolving into an array of items
   * @private
   * @param {Object[]|Promise<Object[]>} itemsOrPromise - the array or a promise
   * of the array of data items
   * @return {Promise<Array>} a Promise resolving into an array of items
   */
  loadData(itemsOrPromise) {
    if (itemsOrPromise && _.isArray(itemsOrPromise)) {
      return Promise.resolve(itemsOrPromise);
    }

    return itemsOrPromise || Promise.resolve([]);
  }

  /**
   * Initializes the {@link https://github.com/Microsoft/projection-grid/blob/master/js/projection/row-tri-state-checkbox.js|checkbox projection}
   * with initial state based on the user supplied {@link Selector~CheckMap|check map}
   * @private
   * @param {template} checkboxTemplate - the checkbox template
   * @return {void}
   */
  initCheckboxes(checkboxTemplate) {
    // Check if checkboxTemplate is null or undefined
    const checkboxTemplatePair = !checkboxTemplate ? {} : { 'row.check.template': checkboxTemplate };
    this.checkbox = new CheckboxProjection({
      // eslint-disable-next-line max-len
      ...checkboxTemplatePair,
      'row.check.click-anywhere': true,
    });
    this.checkbox.on('change:row.check.map', this.onSelectionChange, this);

    this.setCheckMap(this.options.data.checkMap);
  }

  /**
   * Generates and sets a check map with all items checked, if that was requested by the user
   * @private
   * @return {void}
   */
  checkAllIfRequested() {
    if (this.options.data.checkAll) {
      const checkAllMap =
        createCheckAllCheckMap(this.getStats(), this.options.data.checkAll, this.items);

      this.preCheckMap = _.extend({}, this.preCheckMap, checkAllMap);
      const newCheckMap = _.extend({}, this.checkMap, checkAllMap);

      this.checkbox.set('row.check.map', newCheckMap);
    }
  }

  /**
   * Configures columns and the source projection for the grid.
   * @return {SourceProjection} the source projection.
   */
  getProjection() {
    const items = _.isArray(this.items) ? this.items : [];

    // Without the Columns projection the grid will crash/not render
    const columns = new pgrid.projections.Columns({
      columns: {},
    });

    const colQuery = new ColumnQueryableProjection({
      'column.in': ['checkbox', 'Name'],
    });

    let sourceProjection = new SourceProjection({ seed: items })
      .pipe(columns)
      .pipe(colQuery)
      .pipe(this.checkbox);

    if (this.options.templates && this.options.templates.nameTemplate) {
      const columnTemplates = new pgrid.projections.PropertyTemplate({
        'property.template': {
          Name: this.options.templates.nameTemplate,
        },
      });

      sourceProjection = sourceProjection.pipe(columnTemplates);
    }

    return sourceProjection;
  }

  /**
   * Constructs, configures and renders the grid control,
   * used by the selector control to display the data items
   * @private
   * @param {HTMLElement} scrollContainer - the container element that the grid
   * is scrolling within
   * @param {Object} hostElement - the element the grid is appended to
   * @return {external:projection-grid.GridView} a new instance of the GridView class
   */
  renderGrid(scrollContainer, hostElement) {
    const $container = this.$(scrollContainer);
    const $hostElement = this.$(hostElement);

    this.grid = new GridView({
      container: $container,
      projection: this.getProjection(),
      Layout: TableLayout.partial({
        $metadata: {
          class: this.options.styles.list,
          ...this.options.styles.listAttributes,
        },
        template: GridTemplate,
        renderers: [
          Virtualization,
        ],
        columns: {
          checkbox: {
            $metadata: {
              'attr.head': { class: this.options.styles.checkboxColumn, ...this.options.styles.checkboxAttributes },
              'attr.body': { class: this.options.styles.checkboxColumn, ...this.options.styles.checkboxAttributes },
            },
          },
          Name: {
            $metadata: {
              'attr.head': { class: this.options.styles.nameColumn, ...this.options.styles.nameAttributes },
              'attr.body': { class: this.options.styles.nameColumn, ...this.options.styles.nameAttributes },
            },
            $text: this.options.i18n.getString('Selector_Check_All'),
          },
        },
      }),
    });

    // remove head after grid render finished
    // it is causing a bug when selector is on the same page as a legacy grid with sticky header
    this.grid.layout.on('render:finished', () => {
      if (this.options.allowCheckAll !== true) {
        this.grid.$el.find('thead tr').remove();
      }
    });

    // Publishing all grid events as 'grid:grid_event_name'.
    // These events are helpful in synchronizing with the control state's changes
    // eslint-disable-next-line max-len
    // TODO: Task 717192: Provide means to deterministically synchronize with the control's state changes
    this.grid.on('all', (...originalArgs) => {
      const key = `grid:${_.first(originalArgs)}`; // 1st arg in an 'all' handler is the event name
      const args = [key].concat(_.rest(originalArgs));

      this.trigger(...args);
    });

    $hostElement.append(this.grid.el);

    return this.grid.render({ fetch: true });
  }

  /**
   * Renders the selector view.
   * **Note:** The render method is supposed to be called once
   * @return {Selector} the current instance
   */
  render() {
    this.$el.html(template({
      i18n: this.options.i18n,
      itemsDisplayLimit: this.options.data.itemsDisplayLimit,
      showFilterPill: this.filterPill,
    }));

    ko.applyBindings(this.viewModel, this.el);

    if (this.searchBox) {
      this.searchBox.render();
      this.$el.find('.selector__filter').append(this.searchBox.$el);
    }

    if (this.filterPill) {
      this.filterPill.render();
      this.$el.find('.selector__filter-pill').append(this.filterPill.$el);
    }

    this.renderGrid(this.$el.find('.selector__data'), this.$el.find('.selector__grid-host'));

    this.trigger('request:data', this.getFilters());

    return this;
  }

  /**
   * Gets the curent state of the filters.
   * @return {{viewFilter: String, searchFilter: String}} - the current state of the filter settings
   */
  getFilters() {
    return _.clone(this.filterModel.attributes);
  }

  /**
   * Returns the current statistics and state of the selection in the control
   * @return {Selector~FullStat} the current statistics and state of the selection in the control
   */
  getStats() {
    const checkMap = this.checkbox.get('row.check.map');
    const stats = CheckboxProjection.fullStatCheckMap(this.preCheckMap, checkMap);

    return stats;
  }

  /**
   * Causes the control to rerender.
   * This may be needed if construction and first rendering of the control happened,
   * when its parent or itself were hidden, preventing the grid view to properly estimate
   * the displayed size of the list
   * @return {void}
   */
  rerender() {
    if (this.grid) {
      this.grid.rerender();
    }
  }

  /**
   * Handles the selection change event from the grid. Generates {@link Selector~FullStat} and
   * emits a "change:selection" event with it.
   * @private
   * @param {external:projection-grid.BaseProjection} model - the checkbox projection
   * @param {Selector~CheckMap} checkMap - the current check map
   * @return {void}
   */
  onSelectionChange(model, checkMap) {
    const stats = CheckboxProjection.fullStatCheckMap(this.preCheckMap, checkMap);

    this.viewModel.hasChanges(stats.hasChanges());
    this.updateFilterPill(stats);
    this.trigger('change:selection', stats);
  }

  /**
   * Gets the state for the filter pill based on the passed in statisics.
   * @private
   * @param {Object} statistcs - the filter pill relevant statistics
   * @return {{items: Array, disabledItems: Array}} - the filter pill state
   */
  getFilterPillState({ changed = 0, selected = 0 } = {}) {
    const { showSelected, showChanged } = this.options.filter;
    const { i18n } = this.options;

    const items = (showSelected || showChanged ? [{
      id: ViewFilter.all,
      text: i18n.getString('Selector_Filter_All'),
    }] : []).concat(showSelected ? [{
      id: ViewFilter.selected,
      text: i18n.getString('Selector_Filter_Selected', { selected }),
    }] : []).concat(showChanged ? [{
      id: ViewFilter.changed,
      text: i18n.getString('Selector_Filter_Changed', { changed }),
    }] : []);

    const disabledItems = (!selected ? [
      ViewFilter.selected,
    ] : []).concat(!changed ? [
      ViewFilter.changed,
    ] : []);

    return {
      items,
      disabledItems,
    };
  }

  /**
   * Updates te filter pill based on the current selection stats
   * @private
   * @param {Selector~FullStat} stats - the current stats
   * @return {void}
   */
  updateFilterPill(stats) {
    if (!this.filterPill) {
      return;
    }

    const state = this.getFilterPillState({
      changed: _.keys(_.extend({}, stats.changed, stats.added)).length,
      selected: stats.checked.length + stats.indeterminate.length,
    });
    helpers.updateView(this.filterPill, state);
  }
}

Selector.ViewFilter = ViewFilter;
Selector.CheckState = CheckState;
Selector.CheckTransition = CheckTransition;
Selector.statCheckMap = CheckboxProjection.statCheckMap;
